import React, { useState, useEffect, useRef, FC } from "react";
import { Link } from "react-router-dom";
import Content from "../../layout/content/Content";
import { Card, Badge, Row, Col } from "reactstrap";
import Head from "../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  InputSwitch,
  Button,
  TooltipComponent,
} from "../../components/Component";
import QuotationsAside from "./QuotationsAside";
import {
  Progress,
  Table,
  Select,
  Input,
  Flex,
  Spin,
  Empty,
  message,
  Tooltip,
} from "antd";
import type { ColumnType, ColumnGroupType } from "antd/es/table";
import { SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import quotationApis from "../../api/master/quotations";
import moment from "moment";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import { useSelector } from "react-redux";

dayjs.extend(utc);
const { Option } = Select;

interface WidgetData {
  title: string;
  count: number;
  progress: number;
  color: string;
}

interface TableData {
  key: number;
  rfqId: string;
  quotationDescription: string;
  siteName: string;
  deadlineType: string;
  lastSubmissionDate: string;
  costCenterName: string;
  generalLedger: string;
  status: string;
  statusColor: string;
  mappingStatus: string;
}

const RFQView: FC = () => {
  const [sm, setSm] = useState(false);

  const [widgetData, setWidgetData] = useState<WidgetData[]>([]);

  const [filterStatus, setfilterStatus] = useState<any>([]);

  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const [siteOptions, setSiteOptions] = useState<any>([]);
  const fallbackTextAreaRef = useRef<HTMLTextAreaElement | null>(null);
  // Store generated linkIds to avoid repeated API calls
  const [generatedLinks, setGeneratedLinks] = useState<Record<number, string>>(
    {}
  );

  useEffect(() => {
    if (currentUser) {
      const { siteAccessibility } = currentUser;
      if (siteAccessibility) {
        const allSites: any = [];
        const _siteOptions: any = [];
        siteAccessibility?.forEach((item: any) => {
          allSites.push(item.siteID);
          _siteOptions.push({ label: item.siteName, value: item.siteID });
        });
        setSiteOptions([..._siteOptions]);
      }
    }
  }, [currentUser]);

  useEffect(() => {
    fetchCounts();
    fetchStatusFilterOptions();
  }, []);

  const fetchCounts = async () => {
    try {
      const { response, status } = await quotationApis.fetchCounts();
      if (status) {
        const counts = response.data;
        const colors = ["#OO65BD", "#20639B", "#FA991C", "#F6D55C", "#OOCC99"];
        let formatData = counts.map((item: any, index: number) => ({
          title: item.title,
          key: item.title,
          value: item.count,
          percentage: item.progress.toFixed(0),
          color: colors[index % colors.length],
        }));
        setWidgetData(formatData);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchStatusFilterOptions = async () => {
    try {
      const res = await quotationApis.fetchStatusFilterOptions();
      console.log("status");
      const { response, status, message } = res;
      if (status) {
        let options = [];
        let dateresp = response.data;
        console.log("dateresp", dateresp);
        setfilterStatus(dateresp);
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (error) {}
  };

  const navigate = useNavigate();

  const handleRFQClick = (rfq: any) => {
    console.log("Summary", rfq);
    const evalDate = rfq?.evaluationDate;
    if (evalDate) {
      // API stores user-entered time as UTC string, so parse as UTC to get the intended time
      const evalDayjs = dayjs.utc(evalDate);
      // Compare against current UTC time
      const now = dayjs();
      // Convert evalDayjs to local for comparison since API time is actually local time stored as UTC
      const evalLocal = dayjs(evalDayjs.format("YYYY-MM-DD HH:mm:ss"));
      if (now.isBefore(evalLocal)) {
        const diffMs = evalLocal.diff(now);
        const totalMinutes = Math.floor(diffMs / 60000);
        const days = Math.floor(totalMinutes / 1440);
        const hours = Math.floor((totalMinutes % 1440) / 60);
        const minutes = totalMinutes % 60;
        let timeStr = "";
        if (days > 0) timeStr += `${days}d `;
        if (hours > 0) timeStr += `${hours}h `;
        timeStr += `${minutes}m`;
        Swal.fire({
          icon: "info",
          title: "Evaluation Period Not Yet Started",
          html: `<p>You can open this RFQ after the evaluation date & time.</p>
                 <p style="font-size:18px;font-weight:600;color:#2263b3;">Opens in: ${timeStr}</p>
                 <p style="font-size:13px;color:#666;">Evaluation Date: ${evalDayjs.format("DD-MMM-YYYY hh:mm A")}</p>`,
          confirmButtonText: "OK",
        });
        return;
      }
    }
    navigate("/rfq-full-view", { state: { rfq } });
  };

  const generateAndCopyLink = async (record: any) => {
    try {
      const quotationId = record.quotationId;

      // First check if we already generated link in this session
      if (generatedLinks[quotationId]) {
        const baseUrl = window.location.origin;
        const publicUrl = process.env.PUBLIC_URL || "";
        const dynamicUrl = `${baseUrl}${publicUrl}/public-links/rfq/${generatedLinks[quotationId]}`;
        await copyToClipboard(dynamicUrl);
        message.success("Link generated and copied to clipboard!");
        return;
      }

      // Check if link already exists in record (from API response)
      const existingLinkId =
        record.activePublicLink ||
        (record.publicLinks && record.publicLinks.length > 0
          ? record.publicLinks.find((link: any) => link.isActive === 1)?.linkId
          : null);

      if (existingLinkId) {
        // Link already exists, store it and copy
        setGeneratedLinks((prev) => ({
          ...prev,
          [quotationId]: existingLinkId,
        }));
        const baseUrl = window.location.origin;
        const publicUrl = process.env.PUBLIC_URL || "";
        const dynamicUrl = `${baseUrl}${publicUrl}/public-links/rfq/${existingLinkId}`;
        await copyToClipboard(dynamicUrl);
        message.success("Link generated and copied to clipboard!");
        return;
      }

      // No existing link, try to generate new one
      const response = await quotationApis.generateRfqPublicLink(quotationId);

      // Check if response message indicates link already exists
      const responseMessage = response?.message || "";
      const isLinkExistsMessage =
        responseMessage.toLowerCase().includes("already") ||
        responseMessage.toLowerCase().includes("exist") ||
        responseMessage.toLowerCase().includes("active link");

      // Try to extract linkId from various possible response structures
      let linkId =
        response?.response?.linkId ||
        response?.linkId ||
        response?.response?.data?.linkId ||
        response?.data?.linkId;

      // If link already exists message, try to get linkId from response
      if (isLinkExistsMessage) {
        if (linkId) {
          // We have linkId, store it and copy
          setGeneratedLinks((prev) => ({ ...prev, [quotationId]: linkId }));
          const baseUrl = window.location.origin;
          const publicUrl = process.env.PUBLIC_URL || "";
          const dynamicUrl = `${baseUrl}${publicUrl}/public-links/rfq/${linkId}`;
          await copyToClipboard(dynamicUrl);
          message.success("Link generated and copied to clipboard!");
          return;
        } else {
          // No linkId found, just show simple info message (no refresh prompt, no API call needed)
          message.info("Link already exists for this RFQ.");
          return;
        }
      }

      // Success case - new link generated
      if (linkId) {
        // Store the generated linkId to avoid API call on next click
        setGeneratedLinks((prev) => ({ ...prev, [quotationId]: linkId }));
        // Construct URL dynamically based on current environment
        const baseUrl = window.location.origin;
        const publicUrl = process.env.PUBLIC_URL || "";
        const dynamicUrl = `${baseUrl}${publicUrl}/public-links/rfq/${linkId}`;

        // Copy the dynamically constructed URL to clipboard
        await copyToClipboard(dynamicUrl);
        message.success("Link generated and copied to clipboard!");
      } else {
        // Handle case where no linkId found
        const errorMsg = response?.message || "Failed to generate link";
        Swal.fire("Error", errorMsg, "error");
      }
    } catch (error: any) {
      console.error("Error generating link:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to generate link";

      // Check if error is because link already exists
      if (
        errorMessage?.toLowerCase().includes("already") ||
        errorMessage?.toLowerCase().includes("exist") ||
        errorMessage?.toLowerCase().includes("active link")
      ) {
        // Just show simple info message, no refresh prompt
        message.info("Link already exists for this RFQ.");
      } else {
        Swal.fire("Error", errorMessage, "error");
      }
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // Message will be shown by the caller
    } catch (err) {
      // Fallback for older browsers that don't support Clipboard API
      // Create or reuse textarea element via ref
      if (!fallbackTextAreaRef.current) {
        const textArea = document.createElement("textarea");
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "0";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        fallbackTextAreaRef.current = textArea;
      }

      const textArea = fallbackTextAreaRef.current;
      textArea.value = text;
      textArea.focus();
      textArea.select();

      try {
        // Using deprecated execCommand as fallback for older browsers only
        // Type assertion to bypass deprecation warning for older browser compatibility
        const successful = (document as any).execCommand("copy");
        if (!successful) {
          throw new Error("Failed to copy");
        }
        // Message will be shown by the caller
      } catch (error) {
        throw new Error("Failed to copy link");
      }
    }
  };

  // Cleanup ref on unmount
  useEffect(() => {
    return () => {
      if (fallbackTextAreaRef.current) {
        document.body.removeChild(fallbackTextAreaRef.current);
        fallbackTextAreaRef.current = null;
      }
    };
  }, []);

  const columns: (ColumnGroupType<TableData> | ColumnType<TableData>)[] = [
    {
      title: "RFQ ID",
      dataIndex: "rfqId",
      key: "rfqId",
      fixed: "left",
      width: 200,
      align: "left",
      render: (text, record) => {
        return record?.status !== "Draft" && record?.status !== "Rejected" ? (
          <a
            onClick={() => handleRFQClick(record)}
            style={{ color: "#1677ff", fontWeight: "600" }}
          >
            {text}
          </a>
        ) : (
          <>{text}</>
        );
      },
    },
    {
      title: "Site Name",
      dataIndex: "siteName",
      key: "siteName",
      width: 200,
      align: "left",
    },
    // {
    //   title: "Preferred Brands / Make",
    //   dataIndex: "preferredBrands",
    //   key: "preferredBrands",
    //   width: 200,
    //   render: (preferredBrands: any) => {
    //     if (!preferredBrands) return "N/A";
        
    //     // Handle array of objects with brandId and brandName
    //     if (Array.isArray(preferredBrands) && preferredBrands.length > 0) {
    //       const brandNames = preferredBrands.map((brand: any) => {
    //         if (typeof brand === "object" && brand.brandName) {
    //           return brand.brandName;
    //         }
    //         return typeof brand === "string" ? brand : "N/A";
    //       });
    //       return brandNames.join(", ");
    //     }
        
    //     // Handle string format (legacy)
    //     if (typeof preferredBrands === "string" && preferredBrands.trim() !== "") {
    //       return preferredBrands;
    //     }
        
    //     return "N/A";
    //   },
    //   // sorter: (a, b) => a.prefferedBrands.localeCompare(b.prefferedBrands),
    //   // align: "left",
    // },
    {
      title: "Description",
      dataIndex: "quotationDescription",
      key: "quotationDescription",
      width: 250,
      // align: "left",
      render: (text: string, record: any, index: any) => {
        return (
          <>
            <span id={`quotationDescription-tooltip-${index}`}>
              {text?.length > 20 ? `${text?.substring(0, 25)}... ` : text}
            </span>
            {text?.length > 20 && (
              <TooltipComponent
                iconClass="info-circle text-info"
                icon="info"
                id={`quotationDescription-tooltip-${index}`}
                direction="top"
                text={text}
                containerClassName={""}
              />
            )}
          </>
        );
      },
    },
    {
      title: "Contractor",
      dataIndex: "contractorName",
      key: "contractorName",
      width: 200,
    },
    {
      title: "Submission Type",
      dataIndex: "deadlineType",
      key: "deadlineType",
      width: 150,
      align: "left",
    },
    {
      title: "Last Date of Submission",
      dataIndex: "lastSubmissionDate",
      key: "lastSubmissionDate",
      width: 200,
      align: "left",
      render: (text: string) => {
        const date = new Date(text);
        const formattedDate = moment(text).format("DD-MMM-YYYY");
        return <span>{formattedDate}</span>;
      },
    },
    {
      title: "Cost Center",
      dataIndex: "costCenterName",
      key: "costCenterName",
      width: 250,
      align: "left",
      render: (text: string, record: any, index: any) => {
        return (
          <>
            <span id={`costCenterName-tooltip-${index}`}>
              {text?.length > 20 ? `${text?.substring(0, 25)}... ` : text}
            </span>
            {text?.length > 20 && (
              <TooltipComponent
                iconClass="info-circle text-info"
                icon="info"
                id={`costCenterName-tooltip-${index}`}
                direction="top"
                text={text}
                containerClassName={""}
              />
            )}
          </>
        );
      },
    },
    // {
    //   title: "General Ledger",
    //   dataIndex: "generalLedger",
    //   key: "generalLedger",
    //   width: 150,
    //   sorter: (a, b) => a.generalLedger.localeCompare(b.generalLedger),
    //   align: "left",
    // },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      fixed: "right",
      render: (text, record) => {
        return (
          <>
            <div className="justify-start align-center">
              <h6
                className={`tb-status ms-1`}
                style={{
                  fontSize: "14px",
                  color:
                    text === "Awarded"
                      ? "#28A745" // Green
                      : text === "Requested"
                      ? "#007BFF" // Blue
                      : text === "Negotiated"
                      ? "#FD7E14" // Orange
                      : text === "Quoted"
                      ? "#6F42C1" // Purple
                      : text === "Rejected"
                      ? "#DC3545" // Red
                      : text === "Pending"
                      ? "#FFC107" // Yellow
                      : text === "Draft"
                      ? "grey"
                      : "black", // Default color
                }}
              >
                {text == "Quoted" ? "Quotation Received" : text}
              </h6>
            </div>
          </>
        );
      },
      width: 150,
      sorter: (a: any, b: any) =>
        a.mappingStatus.localeCompare(b.mappingStatus),
      align: "left",
    },
    {
      title: "Generate Link",
      key: "generateLink",
      fixed: "right",
      width: 150,
      align: "center",
      render: (text, record: any) => {
        // Only show for Requested, Quoted, or Negotiated status
        const allowedStatuses = ["Requested", "Quoted", "Negotiated"];
        if (!allowedStatuses.includes(record.status)) {
          return null;
        }
        return (
          <>
            <Tooltip title="Generate and copy quotation link">
              <Button
                size="sm"
                onClick={() => generateAndCopyLink(record)}
                style={{ marginRight: "8px" }}
              >
                <Icon name="copy" />
              </Button>
            </Tooltip>
          </>
        );
      },
    },
    {
      title: "Action",
      key: "operation",
      fixed: "right",
      width: 150,
      render: (text, record: any, index) => (
        <>
          <Button
            size="sm"
            // color=""
            onClick={() => {
              navigate(`/view/${record.quotationId}`);
            }}
          >
            <Icon name="focus" />
          </Button>
          {/* <Button
            size="sm"
            // color=""
            onClick={() => {
              navigate(`/viewhistory`);
            }}
          >
            <Icon name="history" title="View Version History" />
          </Button> */}
        </>
      ),
    },
  ];

  return (
    <TransactionFlowDefaultTemplate
      moduleKey="view-rfqs"
      title="Quotations"
      columns={columns}
      sideNavigation={
        <div
          className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
            sm ? "content-active" : ""
          }`}
        >
          <QuotationsAside updateSm={setSm} sm={sm} />
        </div>
      }
      selectFilters={[
        {
          title: "Status",
          placeholder: "Select Status",
          options: filterStatus,
        },
        {
          title: "Site",
          options: siteOptions,
          selectionMode: "multiple",
        },
      ]}
      extra={{
        tableScrollX: 1300,
        dateRange: true,
        excelExport: true,
        dataFilterTootip: "Filtering based on quotation creation date",
      }}
      widget={{
        isActive: true,
        list: widgetData,
      }}
    />
  );
};

export default RFQView;
